import React, { useState, useEffect, useCallback } from 'react';
import { X, MapPin, Loader2, Check, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleMap } from './GoogleMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PinCorrectionPromptModal
 *
 * Two-stage modal triggered after a job is completed when the backend detects
 * the worker's GPS is meaningfully off the saved customer pin (50m–1000m).
 *
 *   Stage 1 — Confirmation prompt:
 *     "We've detected you're at a new location for {customer}. Move the pin?"
 *     [No, keep it]  [Yes, move pin]
 *
 *   Stage 2 — Map editor (shown only after user taps Yes):
 *     - Saved pin (blue)
 *     - Worker's current GPS (orange dot)
 *     - Green draggable pin pre-positioned at worker's GPS
 *     - Tap anywhere on the map → green pin jumps there
 *     - Save calls PATCH /api/customers/{id}/coordinates → locks the pin
 *
 * Props:
 *   suggestion: {
 *     customer_id, customer_name, customer_address,
 *     saved_lat, saved_lng, worker_lat, worker_lng, distance_m
 *   }
 *   onClose:    () => void
 *   onSaved:    (newCoords) => void
 */
const PinCorrectionPromptModal = ({ suggestion, onClose, onSaved, initialStage = 'confirm' }) => {
  const [stage, setStage] = useState(initialStage); // 'confirm' | 'map'
  const [pin, setPin] = useState({
    lat: suggestion?.worker_lat,
    lng: suggestion?.worker_lng,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleMapClick = useCallback(({ lat, lng }) => {
    setPin({ lat, lng });
  }, []);

  const handleMarkerDragEnd = useCallback((m) => {
    setPin({ lat: m.lat, lng: m.lng });
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(
        `${API_URL}/api/customers/${suggestion.customer_id}/coordinates`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ latitude: pin.lat, longitude: pin.lng }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success(`Pin updated for ${suggestion.customer_name}`);
      onSaved?.(pin);
      onClose?.();
    } catch (err) {
      console.error('Save pin error:', err);
      toast.error(`Failed to save: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!suggestion) return null;

  // Stage 1: confirmation prompt
  if (stage === 'confirm') {
    const distLine = suggestion.distance_m
      ? `You're about ${Math.round(suggestion.distance_m)}m from where the pin is currently set.`
      : `No location was set for this customer yet.`;
    return (
      <div
        className="fixed inset-0 bg-black/60 z-[120] flex items-center justify-center p-4"
        data-testid="pin-prompt-confirm"
      >
        <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden">
          <div className="p-5 pb-3 flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <Navigation size={22} className="text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-zinc-900 text-base mb-1">New location detected</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                We've detected you're at a new location for{' '}
                <span className="font-semibold">{suggestion.customer_name}</span>
                {suggestion.customer_address && (
                  <span className="text-zinc-500"> · {suggestion.customer_address.split(',')[0]}</span>
                )}
                . Would you like to move the pin?
              </p>
              <p className="text-xs text-zinc-500 mt-2">{distLine}</p>
            </div>
          </div>
          <div className="px-5 pt-1 pb-4 flex gap-2">
            <button
              onClick={onClose}
              data-testid="pin-prompt-keep"
              className="flex-1 px-4 py-2.5 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-sm font-medium"
            >
              No, keep it
            </button>
            <button
              onClick={() => setStage('map')}
              data-testid="pin-prompt-yes"
              className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
            >
              Yes, move pin
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Stage 2: map editor
  // Saved pin (blue) + draggable new pin (green) shown together so the user
  // can see exactly what's changing.
  const markers = [];
  if (pin.lat && pin.lng) {
    markers.push({
      id: 'new',
      lat: pin.lat,
      lng: pin.lng,
      title: 'New pin location',
      color: '#10b981',
    });
  }
  if (suggestion.saved_lat && suggestion.saved_lng) {
    markers.push({
      id: 'saved',
      lat: suggestion.saved_lat,
      lng: suggestion.saved_lng,
      title: 'Current saved pin',
      color: '#3b82f6',
    });
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="pin-prompt-map"
    >
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl overflow-hidden flex flex-col modal-shell-h">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <MapPin size={18} className="text-emerald-600" />
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-zinc-900 text-base truncate">Move pin</h2>
              <p className="text-xs text-zinc-500 truncate">
                {suggestion.customer_name}
                {suggestion.customer_address && ` · ${suggestion.customer_address.split(',')[0]}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="pin-prompt-close"
            className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-500"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 text-xs text-amber-800 flex-shrink-0">
          Tap on the map or drag the green pin to set this customer's location.
          {suggestion.saved_lat && (
            <span className="ml-1 text-amber-700/70">Blue = current saved pin.</span>
          )}
        </div>

        <div className="flex-1 min-h-[280px]">
          <GoogleMap
            center={pin}
            zoom={18}
            height="100%"
            markers={markers}
            draggableMarkers
            onMarkerDragEnd={handleMarkerDragEnd}
            onMapClick={handleMapClick}
            showControls
          />
        </div>

        <div className="px-4 py-2 text-[11px] text-zinc-500 font-mono border-t border-zinc-100 flex items-center justify-between flex-shrink-0">
          <span>Lat: {pin.lat?.toFixed(6)}</span>
          <span>Lng: {pin.lng?.toFixed(6)}</span>
        </div>

        <div className="px-4 py-3 border-t border-zinc-100 flex items-center gap-2 flex-shrink-0 pb-safe">
          <button
            onClick={onClose}
            disabled={isSaving}
            data-testid="pin-prompt-cancel"
            className="flex-1 px-4 py-2.5 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            data-testid="pin-prompt-save"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50"
          >
            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            Save pin
          </button>
        </div>
      </div>
    </div>
  );
};

export default PinCorrectionPromptModal;
